import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Offcanvas } from 'bootstrap';
import { apiService } from '../services/api';
import { OpportunityDetail, CreateOpportunityData } from '../types/opportunity';
import { criarContratoDaOportunidade, separarCriarContrato } from '../utils/contratoOportunidade';

const OFFCANVAS_EDIT_ID = 'offcanvas_edit';

// Remove backdrops órfãos e restaura o body, mas só se não houver modal/offcanvas aberto
const limparBackdropsOrfaos = () => {
    if (document.querySelector('.offcanvas.show, .modal.show')) return;

    document.querySelectorAll('.offcanvas-backdrop, .modal-backdrop').forEach(el => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
};

// Fecha o offcanvas de edição e resolve só quando ele terminou de fechar.
// Prioriza o botão de fechar (passa pelo mesmo data-api que abriu o offcanvas, então a instância
// é sempre a correta) e, se nada fechar a tempo, força o fechamento.
const fecharOffcanvasEdicao = (): Promise<void> => new Promise(resolve => {
    const el = document.getElementById(OFFCANVAS_EDIT_ID);
    if (!el || !el.classList.contains('show')) {
        resolve();
        return;
    }

    let finalizado = false;
    const finalizar = () => {
        if (finalizado) return;
        finalizado = true;
        el.removeEventListener('hidden.bs.offcanvas', finalizar);

        // Se ainda estiver aberto (hide ignorado ou instância não encontrada), força o fechamento
        if (el.classList.contains('show')) {
            Offcanvas.getInstance(el)?.dispose();
            el.classList.remove('show', 'showing', 'hiding');
            el.setAttribute('aria-hidden', 'true');
            el.removeAttribute('aria-modal');
            el.removeAttribute('role');
        }
        resolve();
    };

    el.addEventListener('hidden.bs.offcanvas', finalizar, { once: true });

    const botaoFechar = el.querySelector<HTMLElement>('[data-bs-dismiss="offcanvas"]');
    if (botaoFechar) {
        botaoFechar.click();
    } else {
        Offcanvas.getInstance(el)?.hide();
    }

    // Fallback caso o evento 'hidden' nunca chegue
    setTimeout(finalizar, 500);
});

export const useOpportunityDetailHandler = (
    opportunity: OpportunityDetail | null,
    // Se informado, atualiza os dados em segundo plano em vez de recarregar a página
    onUpdated?: () => void
) => {
    const navigate = useNavigate();
    const [editingOpportunity, setEditingOpportunity] = useState<OpportunityDetail | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditSuccessModal, setShowEditSuccessModal] = useState(false);
    // Modal exibido quando a edição também gerou um contrato
    const [showContratoSuccessModal, setShowContratoSuccessModal] = useState(false);
    const [contratoGeradoId, setContratoGeradoId] = useState<number | undefined>(undefined);

    // Preparar oportunidade para edição
    const handlePrepareEdit = () => {
        setEditingOpportunity(opportunity);
    };

    // Atualizar oportunidade
    const handleUpdate = async (updatedData: CreateOpportunityData) => {
        if (!opportunity?.id) return;

        try {

            // A flag criarContrato não vai para a API de atualização (igual ao Kanban)
            const { payload, criarContrato } = separarCriarContrato(updatedData);

            console.log('DEBUG updatedData Opportunidade:', JSON.stringify(payload, null, 2));

            await apiService.updateOpportunity(opportunity.id, payload);

            // Cria o contrato se o usuário confirmou isso no formulário (status "Ganha")
            let idContratoCriado: number | undefined;
            if (criarContrato) {
                const resultadoContrato = await criarContratoDaOportunidade(opportunity.id, updatedData);

                if (resultadoContrato.status === 'criado') {
                    idContratoCriado = resultadoContrato.idContrato;
                } else if (resultadoContrato.status === 'erro') {
                    alert(`Oportunidade atualizada, mas não foi possível criar o contrato: ${resultadoContrato.mensagem}`);
                }
                // 'ja_existe': a oportunidade já tem contrato, nada a criar
            }

            // Fecha o offcanvas, limpa qualquer backdrop órfão e só então abre o modal de sucesso.
            // Sem await: o formulário segue salvando os campos personalizados em paralelo.
            fecharOffcanvasEdicao().then(() => {
                limparBackdropsOrfaos();
                setEditingOpportunity(null);

                if (idContratoCriado) {
                    setContratoGeradoId(idContratoCriado);
                    setShowContratoSuccessModal(true);
                } else {
                    setShowEditSuccessModal(true);
                }
            });

            // O refresh da página NÃO acontece aqui: o formulário ainda vai salvar os
            // campos personalizados. Ele avisa quando termina, via handleEditSaved.


        } catch (error) {
            console.error('Erro ao atualizar oportunidade:', error);
            alert('Erro ao atualizar a oportunidade. Por favor, tente novamente.');
        }
    };

    // Chamado pelo formulário quando a edição terminou de salvar tudo
    const handleEditSaved = () => {
        if (onUpdated) {
            // Atualiza em segundo plano, mantendo aba e posição de rolagem
            onUpdated();
        } else {
            // Sem callback: recarrega a página usando o navigate
            setTimeout(() => {
                navigate(0); // O '0' faz com que a página atual seja recarregada
            }, 1500);
        }
    };

    // Fecha o modal de sucesso da edição e garante que nenhum fundo escuro fique para trás
    const handleCloseEditSuccess = () => {
        setShowEditSuccessModal(false);
        // Espera a animação de saída do modal antes de limpar
        setTimeout(limparBackdropsOrfaos, 400);
    };

    // Fecha o modal de contrato criado e limpa o fundo escuro, como no modal de sucesso
    const handleCloseContratoSuccess = () => {
        setShowContratoSuccessModal(false);
        setContratoGeradoId(undefined);
        setTimeout(limparBackdropsOrfaos, 400);
    };

    // Preparar exclusão
    const handlePrepareDelete = () => {
        setShowDeleteModal(true);
    };

    // Confirmar exclusão
    const handleDelete = async () => {
        if (!opportunity?.id) return;

        try {
            setIsDeleting(true);
            await apiService.deleteOpportunity(opportunity.id);

            setShowDeleteModal(false);
            setShowSuccessModal(true);

            // Redirecionar após um breve delay
            setTimeout(() => {
                navigate('/crm/oportunidades');
            }, 1500);

        } catch (error) {
            console.error('Erro ao excluir oportunidade:', error);
            alert('Erro ao excluir a oportunidade. Por favor, tente novamente.');
        } finally {
            setIsDeleting(false);
        }
    };

    const closeModals = () => {
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');

        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
    };

    return {
        editingOpportunity,
        isDeleting,
        showSuccessModal,
        showDeleteModal,
        showEditSuccessModal,
        handlePrepareEdit,
        handleUpdate,
        handleEditSaved,
        handleCloseEditSuccess,
        showContratoSuccessModal,
        contratoGeradoId,
        handleCloseContratoSuccess,
        handlePrepareDelete,
        handleDelete,
        setShowDeleteModal,
        setShowSuccessModal,
        setShowEditSuccessModal,
        closeModals
    };
};